/**
 * Pure helpers for capturing corrective-action (CA) drafts at submit time.
 *
 * Inspectors sometimes type a CA into the open row but never click "+ Add",
 * which used to silently drop it from the report (Jim, 2026-06) — especially
 * when a question already had one committed CA and a second row was left
 * unsaved. These helpers sweep any non-empty draft into the committed
 * corrective actions so nothing the inspector typed is lost. Kept free of React
 * so the behaviour can be unit-tested directly.
 */
import type { CorrectiveActionItem } from './LiteInspectionFlowPage';

export interface CaDraft {
  notes: string;
  location: string;
  actions: CorrectiveActionItem[];
}

export interface CapturableResponse {
  questionId: string;
  value: unknown;
  correctiveActions?: CorrectiveActionItem[];
  notes?: string;
  location?: string;
  requiresCorrection?: boolean;
  timestamp: string;
}

export interface CaptureResult<R> {
  captured: Record<string, R>;
  capturedIds: string[];
}

/** True when a draft row has any typed text worth keeping. */
export function draftHasContent(draft: CaDraft | undefined): boolean {
  return !!draft && (draft.notes.trim() !== '' || draft.location.trim() !== '');
}

/**
 * Return a new responses map with every non-empty CA draft committed as a
 * corrective action on its question. Existing committed CAs are preserved and
 * the captured draft is appended, so an added CA *and* an unsaved row both
 * survive. Drafts that are empty/whitespace are ignored.
 */
export function captureUnsavedCorrectiveActions<R extends CapturableResponse>(
  responses: Record<string, R>,
  caFormState: Record<string, CaDraft>,
  generateId: () => string,
  now: () => string = () => new Date().toISOString()
): CaptureResult<R> {
  const capturedIds = Object.keys(caFormState).filter((id) =>
    draftHasContent(caFormState[id])
  );
  const captured: Record<string, R> = { ...responses };
  for (const id of capturedIds) {
    const draft = caFormState[id];
    const existing = captured[id]?.correctiveActions ?? draft.actions ?? [];
    const merged: CorrectiveActionItem[] = [
      ...existing,
      {
        id: generateId(),
        location: draft.location.trim(),
        notes: draft.notes.trim(),
      },
    ];
    captured[id] = {
      ...captured[id],
      questionId: id,
      value: captured[id]?.value ?? false,
      correctiveActions: merged,
      notes: merged[0]?.notes,
      location: merged[0]?.location,
      requiresCorrection: true,
      timestamp: captured[id]?.timestamp ?? now(),
    } as R;
  }
  return { captured, capturedIds };
}
